import { useCallback, useRef, useState } from 'react'
import type { FtpTransferTask } from './types'
import { ftpUploadWithProgress, ftpDownloadWithProgress } from './api'

function taskId() {
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function useTransferQueue() {
  const [tasks, setTasks] = useState<FtpTransferTask[]>([])
  const [panelOpen, setPanelOpen] = useState(false)
  const processingRef = useRef(false)

  const updateTask = useCallback((id: string, patch: Partial<FtpTransferTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)))
  }, [])

  const processQueue = useCallback(async () => {
    if (processingRef.current) return
    processingRef.current = true
    setPanelOpen(true)

    try {
      while (true) {
        let current: FtpTransferTask | undefined
        setTasks((prev) => {
          current = prev.find((t) => t.status === 'pending')
          if (!current) return prev
          return prev.map((t) =>
            t.id === current!.id ? { ...t, status: 'running' as const, progress: 0 } : t,
          )
        })

        await new Promise((r) => setTimeout(r, 0))
        if (!current) break

        const task = current
        try {
          if (task.direction === 'upload') {
            if (!task.connectionId || !task.localFile) throw new Error('Нет файла для загрузки')
            await ftpUploadWithProgress(
              task.connectionId,
              task.remoteDir,
              task.localFile,
              (p) => updateTask(task.id, { progress: p }),
            )
          } else {
            if (!task.connectionId) throw new Error('Нет соединения')
            const blob = await ftpDownloadWithProgress(
              task.connectionId,
              task.remotePath,
              (p) => updateTask(task.id, { progress: p }),
            )
            if (task.onDownloaded) {
              await task.onDownloaded(blob, task.name)
            }
          }
          updateTask(task.id, { status: 'done', progress: 100 })
        } catch (err) {
          updateTask(task.id, {
            status: 'error',
            error: err instanceof Error ? err.message : 'Ошибка передачи',
          })
        }
      }
    } finally {
      processingRef.current = false
    }
  }, [updateTask])

  const enqueue = useCallback(
    (items: Omit<FtpTransferTask, 'id' | 'status' | 'progress'>[]) => {
      const newTasks: FtpTransferTask[] = items.map((item) => ({
        ...item,
        id: taskId(),
        status: 'pending',
        progress: 0,
      }))
      setTasks((prev) => [...prev, ...newTasks])
      setPanelOpen(true)
      queueMicrotask(() => void processQueue())
      return newTasks
    },
    [processQueue],
  )

  const clearDone = useCallback(() => {
    setTasks((prev) => prev.filter((t) => t.status === 'pending' || t.status === 'running'))
  }, [])

  const activeCount = tasks.filter((t) => t.status === 'pending' || t.status === 'running').length

  return {
    tasks,
    panelOpen,
    setPanelOpen,
    enqueue,
    clearDone,
    activeCount,
  }
}
